import { request, requestBlob } from './core'
import type { AgentProposal, ArchitectureStatus, ProposalPatch, ProposalSuggestion } from './types'

/** A Use Case Hypothesis table row (mirrors `UseCaseRow` in FieldRenderer). */
export interface UseCaseHypothesis {
  name?: string
  description?: string
  type?: 'New' | 'Modernization' | 'Migration'
  impact?: 'H' | 'M' | 'L'
}

/** Snapshot of the AI-proposals loop returned by GET /proposals/ai. */
export interface AIProposalsSnapshot {
  cap: number
  count: number
  remaining_budget: number
  suggestion_status: 'none' | 'running' | 'done' | 'failed'
  suggestion_error?: string | null
  suggestions: ProposalSuggestion[]
  accepted_names: string[]
  skipped_names: string[]
  proposals: AgentProposal[]
  revision: number
  status: string
}

export const proposalsApi = {
  listProposals: (slug: string) =>
    request<{ proposals: AgentProposal[]; complete_count: number }>(
      `/api/assessments/${slug}/proposals`,
    ),

  createProposal: (
    slug: string,
    body: { name: string; description?: string; kind?: string; platform?: string },
    revision?: number,
  ) =>
    request<{ proposal: AgentProposal; revision?: number }>(
      `/api/assessments/${slug}/proposals`,
      { method: 'POST', body: JSON.stringify(body) },
      { ifMatch: revision },
    ),

  /** AI-derive candidate Use Case Hypothesis rows from intake + existing proposals.
   *  Read-only — returns rows for the consultant to merge into the table and Save. */
  generateUseCases: (slug: string) =>
    request<{ use_cases: UseCaseHypothesis[] }>(
      `/api/assessments/${slug}/proposals/use-cases/generate`,
      { method: 'POST' },
    ),

  /** AI-draft a proposal from pasted requirements (description / transcript / PRD).
   *  Returns the appended proposal (incl. a guesstimated ROI) for the wizard to open. */
  draftProposal: (slug: string, text: string) =>
    request<{ proposal: AgentProposal; revision?: number }>(
      `/api/assessments/${slug}/proposals/draft`,
      { method: 'POST', body: JSON.stringify({ text }) },
    ),

  /** AI best-guess fill for one wizard step's fields, grounded in the intake +
   *  the proposal so far. Returns a partial-proposal patch for the wizard to
   *  apply + save. Read-only on the server (no If-Match). */
  analyzeProposalStep: (slug: string, proposalId: string, step: string) =>
    request<{ patch: Partial<AgentProposal> }>(
      `/api/assessments/${slug}/proposals/${proposalId}/analyze`,
      { method: 'POST', body: JSON.stringify({ step }) },
    ),

  /** Kick the AWS architecture-diagram worker for one proposal (202; 409 while
   *  a run is in flight). The worker stages an LLM spec + deterministic PNG. */
  generateArchitecture: (slug: string, proposalId: string) =>
    request<{ status: string }>(
      `/api/assessments/${slug}/proposals/${proposalId}/architecture`,
      { method: 'POST' },
    ),

  getArchitecture: (slug: string, proposalId: string) =>
    request<ArchitectureStatus>(
      `/api/assessments/${slug}/proposals/${proposalId}/architecture`,
    ),

  /** The rendered diagram as a blob (bearer token required — a plain <img src>
   *  can't carry it). Callers turn it into an object URL. */
  getArchitecturePng: (slug: string, proposalId: string) =>
    requestBlob(`/api/assessments/${slug}/proposals/${proposalId}/architecture.png`),

  /** Field-merge one proposal. Nested objects (roi/agentcore/quick/evaluation/
   *  readiness) deep-merge server-side — send only the changed leaves. */
  updateProposal: (
    slug: string,
    proposalId: string,
    patch: ProposalPatch,
    revision?: number,
  ) =>
    request<{ proposal: AgentProposal; complete: boolean; revision?: number }>(
      `/api/assessments/${slug}/proposals/${proposalId}`,
      { method: 'PUT', body: JSON.stringify(patch) },
      { ifMatch: revision },
    ),

  deleteProposal: (slug: string, proposalId: string, revision?: number) =>
    request<{ ok: boolean; remaining: number; revision?: number }>(
      `/api/assessments/${slug}/proposals/${proposalId}`,
      { method: 'DELETE' },
      { ifMatch: revision },
    ),

  beginProposals: (slug: string, revision?: number) =>
    request<{ status: string; revision?: number }>(
      `/api/assessments/${slug}/proposals/begin`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  // --- AI proposals loop (dedicated stage between intake and proposals) ---

  beginAIProposals: (slug: string, revision?: number) =>
    request<{ status: string; revision?: number; cap: number }>(
      `/api/assessments/${slug}/proposals/ai/begin`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  suggestAIProposalsBatch: (slug: string, revision?: number) =>
    request<{ status: string; revision?: number; remaining_budget?: number }>(
      `/api/assessments/${slug}/proposals/ai/suggest`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  getAIProposals: (slug: string) =>
    request<AIProposalsSnapshot>(`/api/assessments/${slug}/proposals/ai`),

  // Per-suggestion accept/reject (shared with the legacy mechanism, but the
  // AIProposals page is now the only caller).
  acceptSuggestion: (slug: string, index: number, revision?: number) =>
    request<{ ok: boolean; proposal: AgentProposal; revision?: number }>(
      `/api/assessments/${slug}/proposals/suggestions/${index}/accept`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  rejectSuggestion: (slug: string, index: number, revision?: number) =>
    request<{ ok: boolean; revision?: number }>(
      `/api/assessments/${slug}/proposals/suggestions/${index}/reject`,
      { method: 'POST' },
      { ifMatch: revision },
    ),
}
